/**
* Structure Cache Utility
* 
* Provides IndexedDB-based caching for spaces, folders, and lists
* to enable instant rehydration and faster loading.
*/

import {
  promisifyRequest,
  clearStore,
  putItem,
  getAllItems,
  getItem,
  deleteDb as deleteDatabase,
  isIndexedDBAvailable as checkIndexedDBAvailable,
} from './indexedDBHelpers';
import { logger } from './logger';
import { DB_NAME, DB_VERSION } from './cacheConstants';
const STRUCTURE_STORE_NAME = 'structure';
const SPACE_ITEMS_STORE_NAME = 'spaceItems';
const STRUCTURE_META_STORE_NAME = 'structureMeta';

interface StructureCacheMetadata {
  lastSyncTime: number;
  totalSpaces: number;
  totalFolders: number;
  totalLists: number;
  version: string;
}

export type CachedStructureItem =
  | { id: string; name: string; type: 'space'; description?: string }
  | { id: string; name: string; type: 'folder'; project_id: string }
  | { id: string; name: string; type: 'list'; project_id: string; folder_id: string | null };

export interface CachedSpace {
  id: string;
  name: string;
  description?: string;
}

export interface CachedFolder {
  id: string;
  name: string;
  project_id: string;
}

export interface CachedList {
  id: string;
  name: string;
  project_id: string;
  folder_id: string | null;
}

// Initialize IndexedDB database with structure stores
async function openDB(hasRetried = false): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = async () => {
      const db = request.result;

      // Self-heal for production deploys: schema might be older and miss stores,
      // which would throw NotFoundError on db.transaction([...]).
      const requiredStores = [
        STRUCTURE_STORE_NAME,
        STRUCTURE_META_STORE_NAME,
        SPACE_ITEMS_STORE_NAME,
      ];
      const missing = requiredStores.filter((s) => !db.objectStoreNames.contains(s));
      if (missing.length > 0) {
        db.close();
        if (hasRetried) {
          reject(new Error(`IndexedDB schema missing stores: ${missing.join(', ')}`));
          return;
        }
        try {
          await deleteDatabase(DB_NAME);
          const repaired = await openDB(true);
          resolve(repaired);
        } catch (e) {
          reject(e);
        }
        return;
      }

      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create unified structure store (spaces, folders, lists)
      if (!db.objectStoreNames.contains(STRUCTURE_STORE_NAME)) {
        const structureStore = db.createObjectStore(STRUCTURE_STORE_NAME, { keyPath: 'id' });
        structureStore.createIndex('type', 'type', { unique: false });
        structureStore.createIndex('name', 'name', { unique: false });
        structureStore.createIndex('project_id', 'project_id', { unique: false });
        structureStore.createIndex('folder_id', 'folder_id', { unique: false });
      }

      // Create structure metadata store if it doesn't exist
      if (!db.objectStoreNames.contains(STRUCTURE_META_STORE_NAME)) {
        db.createObjectStore(STRUCTURE_META_STORE_NAME, { keyPath: 'key' });
      }

      // Create space items store
      if (!db.objectStoreNames.contains(SPACE_ITEMS_STORE_NAME)) {
        const spaceItemsStore = db.createObjectStore(SPACE_ITEMS_STORE_NAME, { keyPath: 'spaceId' });
        spaceItemsStore.createIndex('spaceId', 'spaceId', { unique: true });
      }
    };
  });
}

/**
* Store spaces, folders, and lists in the cache
*/
export async function cacheStructure(
  spaces: CachedSpace[],
  folders: CachedFolder[],
  lists: CachedList[]
): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(
      [STRUCTURE_STORE_NAME, STRUCTURE_META_STORE_NAME],
      'readwrite'
    );
    const structureStore = transaction.objectStore(STRUCTURE_STORE_NAME);
    const metaStore = transaction.objectStore(STRUCTURE_META_STORE_NAME);

    // Clear existing data
    await clearStore(structureStore);

    // Store all items with type field
    const allItems: CachedStructureItem[] = [
      ...spaces.map(space => ({ ...space, type: 'space' as const })),
      ...folders.map(folder => ({ ...folder, type: 'folder' as const })),
      ...lists.map(list => ({ ...list, type: 'list' as const })),
    ];

    await Promise.all(allItems.map(item => putItem(structureStore, item)));

    // Update metadata
    const metadata: StructureCacheMetadata = {
      lastSyncTime: Date.now(),
      totalSpaces: spaces.length,
      totalFolders: folders.length,
      totalLists: lists.length,
      version: '2.0',
    };
    await putItem(metaStore, { key: 'metadata', ...metadata });

    db.close();
  } catch (error) {
    logger.error('Failed to cache structure:', error);
    throw error;
  }
}

/**
* Get all cached items by type
*/
async function getCachedItemsByType<T extends CachedStructureItem>(
  type: 'space' | 'folder' | 'list',
  errorMessage: string
): Promise<T[]> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STRUCTURE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(STRUCTURE_STORE_NAME);
    const index = store.index('type');
    const request = index.getAll(type);
    const items = await promisifyRequest<T[]>(request);
    db.close();
    return items.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    logger.error(errorMessage, error);
    return [];
  }
}

/**
* Get all cached structure items (spaces, folders, lists) in a single transaction
* More efficient when all types are needed together
*/
export async function getAllCachedStructure(): Promise<{
  spaces: CachedSpace[];
  folders: CachedFolder[];
  lists: CachedList[];
}> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STRUCTURE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(STRUCTURE_STORE_NAME);
    const allItems = await getAllItems<CachedStructureItem>(store);
    db.close();

    const spaces: CachedSpace[] = [];
    const folders: CachedFolder[] = [];
    const lists: CachedList[] = [];

    for (const item of allItems) {
      if (item.type === 'space') {
        const { type: _type, ...space } = item;
        spaces.push(space);
      } else if (item.type === 'folder') {
        const { type: _type, ...folder } = item;
        folders.push(folder);
      } else if (item.type === 'list') {
        const { type: _type, ...list } = item;
        lists.push(list);
      }
    }

    return {
      spaces: spaces.sort((a, b) => a.name.localeCompare(b.name)),
      folders: folders.sort((a, b) => a.name.localeCompare(b.name)),
      lists: lists.sort((a, b) => a.name.localeCompare(b.name)),
    };
  } catch (error) {
    logger.error('Failed to get all cached structure:', error);
    return { spaces: [], folders: [], lists: [] };
  }
}

/**
* Get all cached spaces
*/
export async function getCachedSpaces(): Promise<CachedSpace[]> {
  const items = await getCachedItemsByType<CachedStructureItem & { type: 'space' }>('space', 'Failed to get cached spaces:');
  return items.map(({ type: _type, ...space }) => space);
}

/**
* Get all cached folders
*/
export async function getCachedFolders(): Promise<CachedFolder[]> {
  const items = await getCachedItemsByType<CachedStructureItem & { type: 'folder' }>('folder', 'Failed to get cached folders:');
  return items.map(({ type: _type, ...folder }) => folder);
}

/**
* Get all cached lists
*/
export async function getCachedLists(): Promise<CachedList[]> {
  const items = await getCachedItemsByType<CachedStructureItem & { type: 'list' }>('list', 'Failed to get cached lists:');
  return items.map(({ type: _type, ...list }) => list);
}

/**
* Get structure cache metadata
*/
export async function getStructureCacheMetadata(): Promise<StructureCacheMetadata | null> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STRUCTURE_META_STORE_NAME], 'readonly');
    const metaStore = transaction.objectStore(STRUCTURE_META_STORE_NAME);
    const result = await getItem<{ key: string } & StructureCacheMetadata>(metaStore, 'metadata');
    db.close();
    if (result) {
      const { key: _key, ...metadata } = result;
      return metadata;
    }
    return null;
  } catch (error) {
    logger.error('Failed to get structure cache metadata:', error);
    return null;
  }
}

/**
* Sync structure from Supabase to cache
*/
export async function syncStructureFromSupabase(
  getAllSpaces: () => Promise<Array<{ id: string; name: string; description?: string }>>,
  getAllFolders: () => Promise<Array<{ id: string; name: string; project_id: string }>>,
  getAllLists: () => Promise<Array<{ id: string; name: string; project_id: string; folder_id: string | null }>>
): Promise<{ spaces: CachedSpace[]; folders: CachedFolder[]; lists: CachedList[] }> {
  try {
    const [spaces, folders, lists] = await Promise.all([
      getAllSpaces(),
      getAllFolders(),
      getAllLists(),
    ]);

    const cachedSpaces: CachedSpace[] = spaces.map(s => ({ id: s.id, name: s.name, description: s.description }));
    const cachedFolders: CachedFolder[] = folders.map(f => ({
      id: f.id,
      name: f.name,
      project_id: f.project_id,
    }));
    const cachedLists: CachedList[] = lists.map(l => ({
      id: l.id,
      name: l.name,
      project_id: l.project_id,
      folder_id: l.folder_id ?? null,
    }));

    await cacheStructure(cachedSpaces, cachedFolders, cachedLists);

    return {
      spaces: cachedSpaces,
      folders: cachedFolders,
      lists: cachedLists,
    };
  } catch (error) {
    logger.error('Failed to sync structure from Supabase:', error);
    throw error;
  }
}

/**
* Check if IndexedDB is available
*/
export const isIndexedDBAvailable = checkIndexedDBAvailable;

/**
* Cache space items (documents, chats, voice channels, spreadsheets, presentations, folders)
*/
export interface CachedSpaceItems {
  spaceId: string;
  items: Array<{ id: string; name: string; type: string; content?: unknown; parent_id?: string | null }>;
  folders: Array<{ id: string; name: string }>;
  lastSyncTime: number;
}

export async function cacheSpaceItems(spaceId: string, items: {
  items: Array<{ id: string; name: string; type: string; content?: unknown; parent_id?: string | null }>;
  folders: Array<{ id: string; name: string }>;
}): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([SPACE_ITEMS_STORE_NAME], 'readwrite');
    const spaceItemsStore = transaction.objectStore(SPACE_ITEMS_STORE_NAME);

    const cachedItem: CachedSpaceItems = {
      spaceId,
      ...items,
      lastSyncTime: Date.now(),
    };

    await putItem(spaceItemsStore, cachedItem);
    db.close();
  } catch (error) {
    logger.error('Failed to cache space items:', error);
    throw error;
  }
}

export async function getCachedSpaceItems(spaceId: string): Promise<CachedSpaceItems | null> {
  try {
    const db = await openDB();
    const transaction = db.transaction([SPACE_ITEMS_STORE_NAME], 'readonly');
    const spaceItemsStore = transaction.objectStore(SPACE_ITEMS_STORE_NAME);
    const result = await getItem<CachedSpaceItems>(spaceItemsStore, spaceId);
    db.close();
    return result ?? null;
  } catch (error) {
    logger.error('Failed to get cached space items:', error);
    return null;
  }
}

export async function getAllCachedSpaceItems(): Promise<Map<string, CachedSpaceItems>> {
  try {
    const db = await openDB();
    const transaction = db.transaction([SPACE_ITEMS_STORE_NAME], 'readonly');
    const spaceItemsStore = transaction.objectStore(SPACE_ITEMS_STORE_NAME);
    const items = await getAllItems<CachedSpaceItems>(spaceItemsStore);
    db.close();
    return new Map(items.map(item => [item.spaceId, item]));
  } catch (error) {
    logger.error('Failed to get all cached space items:', error);
    return new Map();
  }
}

/**
* Clear structure cache
*/
export async function clearStructureCache(): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(
      [STRUCTURE_STORE_NAME, SPACE_ITEMS_STORE_NAME, STRUCTURE_META_STORE_NAME],
      'readwrite'
    );
    const stores = [
      transaction.objectStore(STRUCTURE_STORE_NAME),
      transaction.objectStore(SPACE_ITEMS_STORE_NAME),
      transaction.objectStore(STRUCTURE_META_STORE_NAME),
    ];

    await Promise.all(stores.map(store => clearStore(store)));
    db.close();
  } catch (error) {
    logger.error('Failed to clear structure cache:', error);
    throw error;
  }
}
